import React, { SyntheticEvent, useMemo } from "react";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { getDefaultAddToCartValues } from "@utils/deliveryMethod";
import { ProductVisibilityTypes } from "@utils/enums";
import { useIsClientSide } from "@utils/hooks/isClient.hook";
import { isConfigurableProduct } from "@utils/productConfiguration/productConfiguration.utils";
import { getPreferredStoreCodeForStock } from "@utils/store";

import { CartModel } from "@redux/modules/api/data/cart/@types/cart.model";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import InstantAddToCartProductTileButton from "@components/Product/ProductTile/components/button/instantAddToCartProductTileButton.container";
import MoreInfoProductTileButton from "@components/Product/ProductTile/components/button/moreInfoProductTileButton.container";
import ProductTileButtonComponent from "@components/Product/ProductTile/components/button/productTileButton.component";

const getTranslationKeyForVisibilityType = (
    productVisibilityType: ProductVisibilityTypes,
) => {
    switch (productVisibilityType) {
        case ProductVisibilityTypes.Offline:
        case ProductVisibilityTypes.NotAvailable:
            return "ecommerce.text.NOT_AVAILABLE";
        case ProductVisibilityTypes.TemporaryNotAvailable:
            return "ecommerce.text.TEMPORARY_NOT_AVAILABLE";
        case ProductVisibilityTypes.OnlyAvailableInStore:
            return "ecommerce.text.ONLY_AVAILABLE_IN_STORE";
        default:
            return null;
    }
};

type ProductTileButtonProps = {
    allowInstantAddToCart: boolean;
    product: ProductTileModel;
    cart?: CartModel | null;
    extraOnClick?: (e: SyntheticEvent<HTMLButtonElement>) => void;
    className?: string;
};

const ProductTileButton = ({
    allowInstantAddToCart,
    product,
    cart,
    extraOnClick,
    className,
}: ProductTileButtonProps) => {
    const { t } = useGranite();
    const preferredStore = getPreferredStoreCodeForStock();
    const isClientSide = useIsClientSide();

    const { defaultDeliveryMethod, defaultQuantity } = useMemo(() => {
        if (!allowInstantAddToCart) {
            return { defaultDeliveryMethod: null, defaultQuantity: null };
        }

        return getDefaultAddToCartValues(product, preferredStore);
    }, [allowInstantAddToCart, preferredStore]);

    const handleOnClick = (e: SyntheticEvent<HTMLButtonElement>) => {
        if (extraOnClick) {
            extraOnClick(e);
        }
    };

    const notAvailableTranslationKey = getTranslationKeyForVisibilityType(
        product.productVisibilityType,
    );

    if (notAvailableTranslationKey) {
        return (
            <ProductTileButtonComponent
                className={className}
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                showOutline={true}
            >
                <>{t(notAvailableTranslationKey)}</>
            </ProductTileButtonComponent>
        );
    }

    if (
        isClientSide &&
        defaultDeliveryMethod &&
        defaultQuantity &&
        !isConfigurableProduct(product.sku) &&
        !product.isAdditionalService
    ) {
        return (
            <InstantAddToCartProductTileButton
                className={className}
                product={product}
                cart={cart}
                extraOnClick={handleOnClick}
                defaultDeliveryMethod={defaultDeliveryMethod}
                defaultQuantity={defaultQuantity}
                preferredStore={preferredStore}
            />
        );
    }

    return (
        <MoreInfoProductTileButton
            className={className}
            onClick={handleOnClick}
        />
    );
};

export default ProductTileButton;
